import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';

export type BrandLifecycleStage = 'registered' | 'ingesting' | 'claimed' | 'complete';
export type LogoAssetVariant = 'primary' | 'wordmark' | 'monochrome' | 'dark' | 'favicon';

export interface LogoAsset {
  url: string;
  format: 'raster' | 'vector';
  updatedAt?: string | null;
}

export type BrandLogoAssets = Partial<Record<LogoAssetVariant, LogoAsset | null>>;

export interface BrandMonitoringSettings {
  priceMonitoring: boolean;
  autoDetectViolations: boolean;
  alertRetailers: boolean;
  weeklyDigest: boolean;
}

export interface BrandReadiness {
  logo: boolean;
  products: boolean;
  ingestion: boolean;
  representative: boolean;
}

export interface BrandClaimSummary {
  status: 'claimed' | 'requested' | 'unclaimed';
  organizationId?: string | null;
  organizationName?: string | null;
  requestId?: string | null;
  /** Owner exists in the registry but their organization has been suspended. */
  ownerSuspended?: boolean;
}

export interface BrandIngestionSummary {
  configured: boolean;
  source: 'Vendor feed' | 'Direct API' | 'Storefront API' | 'Brand site scrape' | 'Retailer scrape' | 'None configured';
  /** An ingester exists but its schedule is switched off: runs only when started by hand. */
  paused?: boolean;
  scraperType?: 'puppeteer' | 'feed' | 'api' | null;
  healthy: boolean | null;
  /** A source is configured but the catalog holds no products for this brand. */
  zeroProducts?: boolean;
  lastRunStatus?: string | null;
  lastRunAt?: string | null;
  lastError?: string | null;
}

/** Which spec mapping normalizes a brand: its own saved mapping, a code adapter, or generic matching only. */
export type SpecMappingSource = 'adapter' | 'saved' | 'generic';

/** Share of a brand's spec labels that reach canonical spec keys (see backend `computeBrandSpecCoverage`). */
export interface BrandSpecCoverage {
  mappingSource: SpecMappingSource;
  explicitFieldCount: number;
  productsSampled: number;
  productsWithSpecs: number;
  labelCount: number;
  canonicalCount: number;
  catchAllCount: number;
  unmappedCount: number;
  percent: number | null;
  productTypes: Array<{ type: string; count: number }>;
  sections: Array<{ key: string; label: string; labels: number }>;
  topUnmappedLabels: Array<{ label: string; products: number }>;
}

/** One scraped label and the Motzu spec it lands on. See `computeBrandSpecLabelMap`. */
export type SpecLabelOutcome = 'canonical' | 'other' | 'unmatched' | 'dropped';
export type SpecLabelMapVia = 'explicit' | 'fuzzy' | 'semantic' | 'other' | 'unmatched' | 'dropped' | 'stored';

export interface BrandSpecLabelRow {
  label: string;
  products: number;
  outcome: SpecLabelOutcome;
  via: SpecLabelMapVia;
  path: string | null;
  motzu: string | null;
  written: boolean;
  sample: string | null;
}

export interface BrandSpecLabelMap {
  mappingSource: SpecMappingSource;
  productsSampled: number;
  productsWithSpecs: number;
  truncated: boolean;
  rows: BrandSpecLabelRow[];
}

export interface BrandDirectoryRow {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string | null;
  logoAssets?: BrandLogoAssets;
  aliases: string[];
  country?: string | null;
  categories: string[];
  tags: string[];
  website?: string | null;
  /** Manufacturer sell-in regions (not Motzu catalog regions). */
  regions?: string[];
  parentCompany?: string | null;
  description?: string | null;
  status: 'active' | 'inactive';
  /** Demo brand: hidden from every store and vendor brand list (MOT-338). Missing on an older API means no. */
  adminOnly?: boolean;
  /** The brand allows sale pricing only inside vendor-approved sale windows (MOT-388). Missing means no. */
  saleWindowsRequired?: boolean;
  /** Products in the catalog statuses (active, archived, discontinued) under every spelling of this brand. Placeholders and drafts excluded. */
  productCount: number;
  /** The subset of `productCount` with status active: what the Product Browser counts. */
  activeProductCount: number;
  readiness: BrandReadiness;
  stage: BrandLifecycleStage;
  claimedBy: BrandClaimSummary;
  ingestion: BrandIngestionSummary;
  specMapping?: { source: SpecMappingSource };
  missingLogo: boolean;
  rasterOnly: boolean;
  hasVector: boolean;
}

/** A product brand string that matches no brand record (also listed, unregistered, in the Product Browser). */
export interface BrandDirectoryUnregisteredBrand {
  name: string;
  productCount: number;
  activeProductCount: number;
}

export interface BrandDirectoryStageCounts {
  registered: number;
  ingesting: number;
  claimed: number;
  complete: number;
  unclaimed: number;
  missingLogos: number;
}

export interface BrandLifecycleItem {
  id: 'added' | 'logo' | 'ingestion' | 'products' | 'claimed';
  label: string;
  done: boolean;
  note?: string | null;
}

export interface CatalogQualityMetric {
  id: 'mpn' | 'gtin' | 'images' | 'description' | 'specs';
  label: string;
  percent: number;
  note?: string | null;
}

export interface BrandIngestionRun {
  id: string;
  status: string;
  type?: string | null;
  createdAt?: string | null;
  completedAt?: string | null;
  productsProcessed?: number;
  productsCreated?: number;
  productsUpdated?: number;
  error?: string | null;
}

export interface BrandDirectoryDetail extends BrandDirectoryRow {
  lifecycle: BrandLifecycleItem[];
  catalogQuality: CatalogQualityMetric[];
  dataQualityPercent: number;
  specCoverage?: BrandSpecCoverage | null;
  retailReach: { carrying: number; totalStores: number };
  ingestionHistory: BrandIngestionRun[];
  outstanding: Array<{ id: string; message: string }>;
  ownership: {
    status: BrandClaimSummary['status'];
    organizationId?: string | null;
    organizationName?: string | null;
    contactName?: string | null;
    contactEmail?: string | null;
    lastSignIn?: string | null;
  };
  monitoring: BrandMonitoringSettings;
}

export interface BrandDashboardViolation {
  id: string;
  retailerId: string;
  retailerName: string;
  listingCount: number;
  seenAt?: string | null;
  underPercent?: number | null;
  underAmount?: number | null;
}

export interface BrandRetailerPerformance {
  organizationId: string;
  organizationName: string;
  compliance: 'violating' | 'drifting' | 'in_policy';
  listings: number;
  violationCount: number;
}

export interface BrandDirectoryDashboard {
  brand: BrandDirectoryRow;
  catalog: { itemCount: number; completePercent: number; source: string };
  mapRules: { count: number; source: string };
  violations: {
    openCount: number;
    retailerCount: number;
    windowDays: number;
    source: string;
    items: BrandDashboardViolation[];
  };
  retailReach: { carrying: number; totalStores: number; source: string };
  monitoring: BrandMonitoringSettings;
  retailerPerformance: BrandRetailerPerformance[];
}

export interface OrganizationDirectoryRow {
  id: string;
  name: string;
  adminEmail?: string | null;
  orgType: 'vendor' | 'retail';
  verification: 'verified' | 'pending_review' | 'rejected';
  verificationLabel: string;
  brandsClaimed: Array<{ brandId: string; brandName: string; status: 'confirmed' | 'requested' }>;
  userCount: number;
  storeCount: number | null;
  status: string;
  createdAt?: string | null;
}

export interface ClaimEvidence {
  emailDomain: { status: 'verified' | 'mismatch' | 'missing'; email?: string; domain?: string; brandHost?: string };
  dealerLetter: { status: 'uploaded' | 'missing'; fileName?: string; storagePath?: string; url?: string };
  dnsTxt: { status: 'verified' | 'pending' | 'failed' | 'missing'; token?: string; host?: string; lastCheckedAt?: string | null };
  competingClaim: { status: 'clear' | 'claimed' | 'pending_other'; ownerOrgName?: string; ownerOrgId?: string };
}

// Organization CRUD/invite — re-homed from the old (unreachable)
// AdminOrganizationManagementPage. Field shapes mirror that page's
// `Organization` / `OrganizationMutationPayload` / `inviteForm`, trimmed to
// what CreateOrganizationRequest / UpdateOrganizationRequest / InviteUserRequest
// (src/types/organization.ts) accept.
export type OrganizationType = 'camera_store' | 'vendor';
export type OrganizationStatus =
  | 'active'
  | 'inactive'
  | 'pending_verification'
  | 'pending_approval'
  | 'suspended';
export type OrganizationVendorType =
  | 'manufacturer'
  | 'distributor'
  | 'wholesaler'
  | 'accessory_brand'
  | 'software_provider';
export type OrganizationRole = 'owner' | 'admin' | 'manager' | 'employee' | 'viewer';

export interface OrganizationContactInfo {
  primaryContact: { name: string; email: string; phone: string };
  website?: string;
}

export interface OrganizationAddress {
  street?: string;
  city: string;
  state: string;
  country: string;
  zipCode?: string;
  /** Older records store the postal code under `zip`. */
  zip?: string;
}

export interface OrganizationBusinessInfo {
  taxId?: string;
  businessLicense?: string;
  description?: string;
}

/** Body for POST /admin/organizations and PUT /admin/organizations/:id. */
export interface OrganizationMutationPayload {
  name: string;
  type: OrganizationType;
  status: OrganizationStatus;
  contactInfo: OrganizationContactInfo;
  address: OrganizationAddress;
  businessInfo: OrganizationBusinessInfo;
  vendorType?: OrganizationVendorType;
  catalogBrandIds?: string[];
}

/** GET /organizations/:id — the full organization record, used to prefill the edit dialog. */
export interface OrganizationDetail {
  id: string;
  name: string;
  type: OrganizationType | 'store';
  status: OrganizationStatus;
  contactInfo?: Partial<OrganizationContactInfo>;
  address?: Partial<OrganizationAddress>;
  businessInfo?: Partial<OrganizationBusinessInfo>;
  vendorInfo?: { description?: string };
  vendorType?: OrganizationVendorType;
  catalogBrandIds?: string[];
}

/** Body for POST /admin/organizations/:id/invite. */
export interface InviteOrganizationUserPayload {
  email: string;
  role: OrganizationRole;
  notes?: string;
}

/** An emailed brand rep invitation (flag `brand_rep_invites`); the API never returns the token. */
export interface BrandRepInvitation {
  id: string;
  brandId: string;
  brandName: string;
  email: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  invitedBy: string;
  invitedAt: string;
  expiresAt: string;
  note?: string;
  resentAt?: string;
  acceptedAt?: string;
}

/** Stamped on a claim request whose rep signed up from an invitation to the same address. */
export interface BrandRepInviteEvidence {
  invitationId: string;
  email: string;
  invitedBy: string;
  invitedAt: string;
}

export interface VendorApprovalRequest {
  id: string;
  adminInvite?: BrandRepInviteEvidence;
  organizationId: string;
  userId: string;
  vendorName: string;
  brandName: string;
  brandId?: string;
  status: 'pending' | 'approved' | 'rejected' | 'needs_info';
  submittedAt?: string | Date | null;
  reviewedAt?: string | Date | null;
  reviewNotes?: string;
  rejectionReason?: string;
  evidence?: ClaimEvidence;
  companyInfo?: { legalName?: string; website?: string; description?: string };
  contactInfo?: {
    primaryContact?: { name?: string; email?: string; phone?: string; title?: string };
  };
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await makeApiCall(endpoint, {
    ...options,
    headers: {
      ...(await getAuthHeaders()),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error?.message || payload.message || `HTTP ${response.status}`);
  }
  return payload.data as T;
}

export const brandDirectoryApi = {
  list: (search?: string) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    const suffix = params.toString() ? `?${params}` : '';
    return request<{ brands: BrandDirectoryRow[]; counts: BrandDirectoryStageCounts; unregisteredBrands?: BrandDirectoryUnregisteredBrand[] }>(`/admin/brand-directory${suffix}`);
  },
  get: (brandId: string) => request<BrandDirectoryDetail>(`/admin/brand-directory/${brandId}`),
  specMap: (brandId: string) => request<BrandSpecLabelMap>(`/admin/brand-directory/${brandId}/spec-map`),
  dashboard: (brandId: string) => request<BrandDirectoryDashboard>(`/admin/brand-directory/${brandId}/dashboard`),
  updateLogoAsset: (brandId: string, variant: LogoAssetVariant, body: { url: string; format?: 'raster' | 'vector' }) =>
    request(`/admin/brand-directory/${brandId}/logo-assets/${variant}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  updateMonitoring: (brandId: string, body: Partial<BrandMonitoringSettings>) =>
    request<BrandMonitoringSettings>(`/admin/brand-directory/${brandId}/monitoring`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  listOrganizations: () =>
    request<{ organizations: OrganizationDirectoryRow[]; awaitingReview: number; pendingClaims: number }>(
      '/admin/organizations/directory'
    ),
  getOrganization: (organizationId: string) =>
    request<OrganizationDetail>(`/organizations/${organizationId}`),
  createOrganization: (body: OrganizationMutationPayload) =>
    request<OrganizationDetail>('/admin/organizations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  updateOrganization: (organizationId: string, body: Partial<OrganizationMutationPayload>) =>
    request<OrganizationDetail>(`/admin/organizations/${organizationId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  deleteOrganization: (organizationId: string) =>
    request<void>(`/admin/organizations/${organizationId}`, { method: 'DELETE' }),
  inviteOrganizationUser: (organizationId: string, body: InviteOrganizationUserPayload) =>
    request<unknown>(`/admin/organizations/${organizationId}/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  listApprovals: (status?: string) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const suffix = params.toString() ? `?${params}` : '';
    return request<VendorApprovalRequest[]>(`/vendor-approval${suffix}`);
  },
  pendingApprovals: () => request<VendorApprovalRequest[]>('/vendor-approval/pending'),
  approveRequest: (requestId: string, notes?: string, forceTransfer?: boolean) =>
    request(`/vendor-approval/${requestId}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes, forceTransfer }),
    }),
  returnToPending: (requestId: string) =>
    request(`/vendor-approval/${requestId}/return-to-pending`, { method: 'POST' }),
  rejectRequest: (requestId: string, reason: string) =>
    request(`/vendor-approval/${requestId}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }),
  requestInfo: (requestId: string, notes: string) =>
    request(`/vendor-approval/${requestId}/request-info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes }),
    }),
  verifyDns: (requestId: string) =>
    request<ClaimEvidence>(`/vendor-approval/${requestId}/verify-dns`, { method: 'POST' }),
  /** `enabled: false` means the flag is off: the brand page falls back to copying the sign-up link. */
  listRepInvites: (brandId: string) =>
    request<{ enabled: boolean; invitations: BrandRepInvitation[] }>(`/admin/brands/${encodeURIComponent(brandId)}/rep-invites`),
  sendRepInvite: (brandId: string, body: { email: string; note?: string }) =>
    request<{ invitation: BrandRepInvitation; emailSent: boolean }>(`/admin/brands/${encodeURIComponent(brandId)}/rep-invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  resendRepInvite: (brandId: string, invitationId: string) =>
    request<{ invitation: BrandRepInvitation; emailSent: boolean }>(
      `/admin/brands/${encodeURIComponent(brandId)}/rep-invites/${encodeURIComponent(invitationId)}/resend`,
      { method: 'POST' }
    ),
  revokeRepInvite: (brandId: string, invitationId: string) =>
    request<{ invitation: BrandRepInvitation }>(
      `/admin/brands/${encodeURIComponent(brandId)}/rep-invites/${encodeURIComponent(invitationId)}`,
      { method: 'DELETE' }
    ),
};

export const LOGO_VARIANT_META: Record<LogoAssetVariant, { label: string; hint: string }> = {
  primary: { label: 'Primary mark', hint: 'Square, transparent, 512px+' },
  wordmark: { label: 'Wordmark', hint: 'Horizontal lockup for headers' },
  monochrome: { label: 'Monochrome', hint: 'Single colour for price sheets' },
  dark: { label: 'Dark background', hint: 'Knockout for dark UI and email' },
  favicon: { label: 'Favicon 64px', hint: 'Cropped mark for browser tabs' },
};
